import { useState, useMemo } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  exportDemographicData,
  getDemographicsData,
  type ExportDemographicParams,
} from '@/api/sponsorApi'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import SponsorshipSelect, {
  type SponsorShipEventSelectValue,
} from './SponsorshipSelect'
import { CalendarIcon } from 'lucide-react'
import {
  getReportDateRange,
  handleAxiosError,
  safeDownloadFile,
} from '@/utils/helper'
import { SPONSOR_QUERY_KEYS } from '@/api/queryKeys'
import { SPONSOR_REPORT_TYPES } from '@/constants/sponsor'

import { ExportIcon } from '../Icons'
import { STATISTICS_DATA_FETCH_TIMEOUT } from '@/constants'
import { DemographicsDistributionCard } from './DemographicsDistributionCard'
import { processDemographicsData } from '@/utils/demographics'
// ...existing code...

const reportTypes = [
  { value: SPONSOR_REPORT_TYPES.LAST_10_DAYS, label: 'Last 10 Days' },
  { value: SPONSOR_REPORT_TYPES.LAST_30_DAYS, label: 'Last 30 Days' },
  { value: SPONSOR_REPORT_TYPES.LAST_90_DAYS, label: 'Last 90 Days' },
  { value: SPONSOR_REPORT_TYPES.ALL_TIME, label: 'All time' },
] as const

type ReportTypeValue =
  (typeof SPONSOR_REPORT_TYPES)[keyof typeof SPONSOR_REPORT_TYPES]

export default function DemographicsData() {
  const [reportType, setReportType] = useState<ReportTypeValue>(
    reportTypes[0].value
  )
  const [selectedEvent, setSelectedEvent] =
    useState<SponsorShipEventSelectValue | null>(null)

  const { startDate, endDate } = useMemo(
    () => getReportDateRange(reportType),
    [reportType]
  )
  const isAllTime = reportType === SPONSOR_REPORT_TYPES.ALL_TIME

  const {
    data: demographicsRaw,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      SPONSOR_QUERY_KEYS.demographicsData,
      selectedEvent,
      isAllTime ? undefined : startDate,
      isAllTime ? undefined : endDate,
    ],
    queryFn: () =>
      getDemographicsData({
        ...(selectedEvent && { eventId: selectedEvent?.id }),
        ...(!isAllTime && { startDate, endDate }),
      }),
    refetchInterval: STATISTICS_DATA_FETCH_TIMEOUT,
    refetchIntervalInBackground: true,
  })

  const { geographicList, ageChartData } = useMemo(
    () => processDemographicsData(demographicsRaw),
    [demographicsRaw]
  )

  const exportMutation = useMutation({
    mutationFn: (params: ExportDemographicParams) =>
      exportDemographicData(params),
    onSuccess: blob => {
      safeDownloadFile({ data: blob, fileName: 'demographics-export.csv' })
    },
    onError: error => {
      handleAxiosError(
        'Export Failed',
        error,
        'No demographic data available for export.'
      )
    },
  })

  const handleExportCSV = () => {
    exportMutation.mutate({
      ...(selectedEvent && { eventId: selectedEvent?.id }),
      ...(!isAllTime && { startDate, endDate }),
    })
  }

  const handleReportTypeChange = (value: string) => {
    setReportType(value as ReportTypeValue)
  }

  const handleEventSelectChange = (
    event: SponsorShipEventSelectValue | SponsorShipEventSelectValue[] | null
  ) => {
    let selectedEventData: SponsorShipEventSelectValue | null = null
    if (event) {
      if (Array.isArray(event)) {
        selectedEventData = event[0] || null
      } else {
        selectedEventData = event
      }
    }

    setSelectedEvent(selectedEventData)
  }

  return (
    <section className="space-y-6 pb-6" aria-label="Demographics Data">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-ss-neutral-950 leading-7">
          Audience Demographics
        </h2>
        <div className="flex items-center gap-3">
          <Select
            value={reportType}
            onValueChange={handleReportTypeChange}
            aria-label="Select report type"
          >
            <SelectTrigger
              size="lg"
              id="report-type"
              className="w-40 h-9 text-sm font-medium flex items-center gap-2 no-arrow"
            >
              <SelectValue placeholder="Select a report type" />
              <CalendarIcon className="ml-2 w-4 h-4 text-ss-neutral-600" />
            </SelectTrigger>
            <SelectContent>
              {reportTypes.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <SponsorshipSelect
            variant="compact"
            value={selectedEvent}
            onValueChange={handleEventSelectChange}
            placeholder="All Events"
            enableSearch={true}
          />

          <Button
            variant="default"
            onClick={handleExportCSV}
            aria-label="Export table as CSV"
          >
            <ExportIcon className="h-4 w-4" aria-hidden="true" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Demographics Cards */}
      <DemographicsDistributionCard
        isLoading={isLoading}
        isError={isError}
        geographicList={geographicList}
        ageChartData={ageChartData}
      />
    </section>
  )
}
